import { createStore } from '@oneappexchange/lwc-wired';
import { Draft } from 'immer';
import { IS_DEVELOPMENT } from '~/modules/config';
import { getUniqueIteratorKey } from '~/modules/utilities/lwcUtilities';
import { logger } from '../utilities/logger';

export enum NotificationType {
    Error = 'error',
    Success = 'success',
}

export interface Notification {
    iteratorKey: string;
    type: NotificationType;
    message: string;
    previousFocusedElement: HTMLElement | null;
    title: string | null;
    // Overrides the type-based default icon (e.g. 'copy' instead of success's default 'check').
    icon?: string;
}

type NotificationState = {
    notifications: Notification[];
    id: number;
};

const initialState: NotificationState = {
    notifications: [],
    id: 0,
};

export const notificationsStore = createStore(
    initialState,
    (set: SetState<NotificationState>, get: GetState<NotificationState>) => ({
        reset() {
            set(function reset() {
                return initialState;
            });
        },
        addNotification(
            message: string,
            type: NotificationType,
            options: {
                previousFocusedElement: Notification['previousFocusedElement'];
                title: string;
                icon?: string;
                // When set, the notification removes itself after this many ms — for
                // confirmations (e.g. "Copied to Clipboard") that don't need a manual close.
                autoDismissMs?: number;
            } = {
                previousFocusedElement: null,
                title: '',
            },
        ) {
            const { previousFocusedElement, title, icon, autoDismissMs } = options;
            const iteratorKey = getUniqueIteratorKey();
            let wasAdded = false;
            set(function addNotification(draft) {
                if (
                    draft.notifications.length === 0 ||
                    !notificationAlreadyExists(message, type, draft.notifications)
                ) {
                    draft.id++;
                    wasAdded = true;
                    draft.notifications.push({
                        iteratorKey,
                        message: message,
                        type: type,
                        title: title,
                        icon,
                        previousFocusedElement: previousFocusedElement as Draft<HTMLElement>,
                    });
                } else {
                    logger.warn(`Attempted to insert duplicate notification: message=${message}, type=${type},`);
                }
            });
            if (wasAdded && autoDismissMs) {
                // Deferred, so referencing the store by name (not `get`, which only exposes state)
                // is safe — by the time this fires, module init has long finished.
                setTimeout(() => notificationsStore.removeNotification(iteratorKey), autoDismissMs);
            }
            return get().id;
        },
        removeNotification(iteratorKey: string) {
            set(function removeNotification(draft) {
                for (let i = 0; i < draft.notifications.length; i++) {
                    if (draft.notifications[i].iteratorKey === iteratorKey) {
                        draft.notifications.splice(i, 1);
                        return;
                    }
                }
            });
        },
    }),
    'notificationStore',
    IS_DEVELOPMENT,
);

export function selectNotifications(state: NotificationState) {
    return state.notifications;
}

function notificationAlreadyExists(newMessage: string, newType: NotificationType, notifications: Notification[]) {
    return notifications.some(({ message, type }) => message === newMessage && type === newType);
}
